import { t } from "i18next";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import moment from 'moment';
import { Button, Form } from "react-bootstrap";
import { Competition } from "../../../../interfaces/competition";
import { getCompetitions } from "../../../../services/competition";
import { AdminCompetitionNew } from "./competition-new";
import { AdminCompetitionDelete } from "./competition-delete";
import { AdminCompetitionEdit } from "./competition-edit";
import { useNavigate } from "react-router-dom";
import { tableStyles, getPaginationOptions } from "../../../../util/data-table";

export function AdminCompetitionList() {
    const navigate = useNavigate();

    const [competitions, setCompetitions] = useState<Competition[]>([]);
    const [showAddNew, setShowAddNew] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(0);


    const CustomDateStart = (row: any ) => (
        <span>
            { moment(row.row.date_start).format("DD/MM/YYYY") }
        </span>
    );

    const CustomDateEnd = (row: any ) => (
        <span>
            { moment(row.row.date_end).format("DD/MM/YYYY") }
        </span>
    );
    
    const CustomChangedAt = (row: any ) => (
        <span>
            { moment(row.row.changed_at).format("DD/MM/YYYY HH:mm") }
        </span>
    );
    
    const CustomActions = (row: any) => (
        <span>
            <a href="#" className="icon-link edit" title={ t("Edit") } onClick={ handleEdit } data-id={ row.row.id }>
                <i className="bi bi-pencil-square"></i>
            </a>
            <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDelete } data-id={ row.row.id }>
                <i className="bi bi-trash"></i>
            </a>
        </span>
    );

    const columns = [
        {
            id: 'id',
            name: '#',
            maxWidth: '80px',
            selector: (row: any) => row.id,
            sortable: true
        },
        {
            id: 'name',
            name: t("Name"),
            selector: (row: any) => row.name,
            sortable: true
        },
        {
            id: 'date_start',
            name: t("Start date"),
            maxWidth: '150px',
            selector: (row: any) => row.date_start,
            sortable: true,
            cell: (row: any) => <CustomDateStart row={row} />,
        },
        {
            id: 'date_end',
            name: t("End date"),
            maxWidth: '150px',
            selector: (row: any) => row.date_end,
            sortable: true,
            cell: (row: any) => <CustomDateEnd row={row} />,
        },
        {
            id: 'changed_at',
            name: t('changed_at'),
            maxWidth: '200px',
            selector: (row: any) => row.changed_at,
            sortable: true,
            cell: (row: any) => <CustomChangedAt row={row} />,
        },
        {
            id: 'actions',
            name: t("actions"),
            maxWidth: '80px',
            cell: (row: any) => <CustomActions row={row} />,
        }

    ];

    useEffect(() => {
         getCompetitions()
            .then(response => {
                setCompetitions(response.data);
            })
            .catch(error => {
                localStorage.removeItem('token');
                navigate("/");
                console.log(error);
            });
    }, []);

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;
        const passed_competitions = event.target.passed_competitions.checked;

        getCompetitions(s, passed_competitions)
            .then(response => {
                setCompetitions(response.data);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAddNew(true)
    }

    const handleAddNewOnSave = (s: Competition) => {
        const newCompetitions = [
            s,
            ...competitions
        ];
        setCompetitions(newCompetitions);
        setShowAddNew(false);
    }

    const handleAddNewOnClose = () => {
        setShowAddNew(false);
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowDelete(true);
    }

    const handleDeleteOnDelete = (id: number) => {
        setSelectedId(0);
        const newCompetitions = competitions.filter((m: Competition) => m.id != id);
        setCompetitions(newCompetitions);
        setShowDelete(false);
    }

    const handleDeleteOnClose = () => {
        setSelectedId(0);
        setShowDelete(false);
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedCompetition: Competition) => {
        setSelectedId(0);
        const newCompetitions = competitions.map((m: Competition) => {
            if (m.id == changedCompetition.id) {
                return changedCompetition;
            } else {
                return m;
            }
        });
        setCompetitions(newCompetitions);
        setShowEdit(false);
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    return (
        <div className="competitions-list-container">
            <h1>
                { t('competitions')}
            </h1>
            <div className="filters-container">
                <Form onSubmit={ handleSearchSubmit } className="filters-form">
                    <Form.Group className="field-search" controlId="search">
                        <Form.Control type="search" placeholder={ t("Write to search") } />
                    </Form.Group>
                    <Form.Group className="field-passed-competitions" controlId="passed_competitions">
                        <Form.Check type="checkbox" label={ t("passed_competitions") } name="passed_competitions" />
                    </Form.Group>
                    <div className="actions">
                        <Button variant="primary" type="submit">
                            { t("search") }
                        </Button>
                    </div>
                </Form>
            </div>
            <div className="table-container">
                <DataTable
                    columns={ columns }
                    data={competitions}
                    // selectableRows={ true }
                    // selectableRowsHighlight={ true }
                    responsive={ true }
                    striped={ true }
                    highlightOnHover={ true }
                    defaultSortFieldId={ 'changed_at' }
                    defaultSortAsc={ false }
                    pagination={ true }
                    customStyles={ tableStyles }
                    paginationComponentOptions={ getPaginationOptions() }
                />
            </div>            
            <a className="admin-add-new" href="#" onClick={ handleAddNew }>
                <i className="bi bi-plus-circle"></i>
            </a>
            { showAddNew &&
                <AdminCompetitionNew onSave={ handleAddNewOnSave } onClose={ handleAddNewOnClose } />
            }
            { showDelete && (selectedId != 0) &&
                <AdminCompetitionDelete id={ selectedId } onDelete={ handleDeleteOnDelete } onClose={ handleDeleteOnClose } />
            }
            { showEdit && (selectedId != 0) &&
                <AdminCompetitionEdit id={ selectedId } onSave={ handleEditOnSave } onClose={ handleEditOnClose } />
            }
        </div>

    )
}
